import React, { useCallback, useRef, useState } from "react";
import { Alert, Button, Card, CardBody, CardHeader } from "reactstrap";
import ScanConfirmModal from "./store/components/ScanConfirmModal";
import { updateOpsPartItem, updateStockItem } from "./store/production/service";
import { toErrorText } from "./store/pages/storeSalesDashboardHelpers";
import {
  useQrScanner,
  QR_SCANNER_CONTAINER_CLASS,
} from "./store/utils/useQrScanner";

const SCANNER_ELEMENT_ID = "panel-scan-qr-reader";
const IN_STOCK_STATUS = "in_stock";
const PART_SCAN_PREFIX = "P-";
const STOCK_SCAN_PREFIX = "S-";

const parseScan = (text) => {
  const trimmed = String(text ?? "").trim();

  if (trimmed.startsWith(PART_SCAN_PREFIX)) {
    const itemId = trimmed.slice(PART_SCAN_PREFIX.length).trim();
    if (!/^\d+$/.test(itemId)) return null;

    return { kind: "part", itemId };
  }

  if (trimmed.startsWith(STOCK_SCAN_PREFIX)) {
    const itemId = trimmed.slice(STOCK_SCAN_PREFIX.length).trim();
    if (!/^\d+$/.test(itemId)) return null;

    return { kind: "stock", itemId };
  }

  return null;
};

const PanelScan = () => {
  const showTestScanInput = true;
  const lastHandledScanRef = useRef(null);
  const [scanMessage, setScanMessage] = useState(null);
  const [testScanText, setTestScanText] = useState("");
  const [pendingScan, setPendingScan] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleScanSuccess = useCallback((decodedText) => {
    const trimmed = String(decodedText ?? "").trim();
    if (!trimmed || trimmed === lastHandledScanRef.current) return;

    setScanMessage(null);
    lastHandledScanRef.current = trimmed;
    setPendingScan({ text: trimmed });
  }, []);

  const { scannerReady, scannerError, clearScannerError } = useQrScanner({
    elementId: SCANNER_ELEMENT_ID,
    enabled: !pendingScan,
    onScan: handleScanSuccess,
  });

  const cancelConfirm = () => {
    if (confirmLoading) return;

    setPendingScan(null);
    lastHandledScanRef.current = null;
  };

  const confirmScan = async () => {
    if (!pendingScan || confirmLoading) return;

    const { text } = pendingScan;
    const parsed = parseScan(text);

    if (!parsed) {
      setPendingScan(null);
      lastHandledScanRef.current = null;
      setScanMessage({
        type: "danger",
        text: "Invalid panel scan format.",
      });
      return;
    }

    setConfirmLoading(true);

    try {
      if (parsed.kind === "stock") {
        await updateStockItem(parsed.itemId, IN_STOCK_STATUS);
      } else {
        await updateOpsPartItem(parsed.itemId, IN_STOCK_STATUS);
      }
      setPendingScan(null);
    } catch (err) {
      setPendingScan(null);
      lastHandledScanRef.current = null;
      setScanMessage({
        type: "danger",
        text: toErrorText(
          err,
          parsed.kind === "stock"
            ? "Unable to update stock item."
            : "Unable to update part item."
        ),
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  const resetForRescan = () => {
    lastHandledScanRef.current = null;
    clearScannerError();
    setScanMessage(null);
    setPendingScan(null);
  };

  const handleTestScanSubmit = () => {
    const text = testScanText.trim();
    if (!text) return;

    lastHandledScanRef.current = null;
    handleScanSuccess(text);
    setTestScanText("");
  };

  return (
    <div className="min-vh-100 w-100 bg-light">
      <div className="container py-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <h1 className="h4 mb-0">Panel Scan</h1>
          <Button color="light" size="sm" outline onClick={resetForRescan}>
            Scan again
          </Button>
        </div>

        {scannerError ? (
          <Alert color="warning" fade={false} className="mb-4">
            {scannerError}
          </Alert>
        ) : null}

        {scanMessage ? (
          <Alert color={scanMessage.type} fade={false} className="mb-4">
            {scanMessage.text}
          </Alert>
        ) : null}

        <Card className="shadow-sm mb-4">
          <CardHeader className="bg-white fw-semibold">Camera</CardHeader>
          <CardBody>
            {!scannerReady && !scannerError ? (
              <p className="text-muted mb-3">Starting camera…</p>
            ) : null}
            <div
              id={SCANNER_ELEMENT_ID}
              className={QR_SCANNER_CONTAINER_CLASS}
            />
          </CardBody>
        </Card>

        {showTestScanInput && (
          <Card className="shadow-sm mb-4">
            <CardHeader className="bg-white fw-semibold">Test scan</CardHeader>
            <CardBody>
              <div className="d-flex flex-wrap gap-2 align-items-end">
                <div className="flex-grow-1">
                  <label
                    htmlFor="panel-scan-test-input"
                    className="form-label small text-muted mb-1"
                  >
                    Scan text
                  </label>
                  <input
                    id="panel-scan-test-input"
                    type="text"
                    className="form-control font-monospace"
                    value={testScanText}
                    disabled={confirmLoading || Boolean(pendingScan)}
                    onChange={(event) => setTestScanText(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleTestScanSubmit();
                      }
                    }}
                  />
                </div>
                <Button
                  color="primary"
                  outline
                  disabled={
                    confirmLoading ||
                    Boolean(pendingScan) ||
                    !testScanText.trim()
                  }
                  onClick={handleTestScanSubmit}
                >
                  Submit
                </Button>
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      <ScanConfirmModal
        isOpen={pendingScan != null}
        scanText={pendingScan?.text ?? ""}
        title="Confirm panel scan"
        confirmLabel="Mark in stock"
        loading={confirmLoading}
        onConfirm={confirmScan}
        onCancel={cancelConfirm}
      />
    </div>
  );
};

export default PanelScan;
